import React, { useState, useRef, useEffect } from "react"
import { IoChevronDown, IoClose, IoCheckmark } from "react-icons/io5"
import styles from "./ColorPicker.module.css"

export interface ColorOption {
  name: string
  value: string
  hex: string
}

interface ColorPickerProps {
  selectedColor: ColorOption | null
  onColorChange: (color: ColorOption | null) => void
  className?: string
  placeholder?: string
  keyLabel?: keyof ColorOption
  withoutColorLabel?: string
  COLORS: ColorOption[]
}

export default function ColorPicker({
  selectedColor,
  onColorChange,
  className = "",
  // placeholder = "Sin color",
  keyLabel = "name",
  withoutColorLabel = "Sin Color",
  COLORS
}: ColorPickerProps) {
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside)
    }

    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [isOpen])

  const handleColorSelect = (color: ColorOption | null) => {
    onColorChange(color)
    setIsOpen(false)
  }

  return (
    <div className={`${styles.container} ${className}`} ref={dropdownRef}>
      {/* Trigger Button */}
      <button type="button" onClick={() => setIsOpen(!isOpen)} className={styles.trigger}>
        {/* Color Preview */}
        <div
          className={`${styles.colorPreview} ${!selectedColor ? styles.colorPreviewEmpty : ""}`}
          style={{
            backgroundColor: selectedColor?.hex || "transparent",
          }}
        >
          {!selectedColor && <IoClose size={12} className={styles.closeIcon} />}
        </div>

        <span className={`${styles.triggerText} ${!selectedColor ? styles.triggerTextPlaceholder : ""}`}>
          {/* {selectedColor ? selectedColor.name : placeholder} */}
        </span>

        <IoChevronDown size={16} className={`${styles.chevron} ${isOpen ? styles.chevronOpen : ""}`} />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className={styles.dropdown}>
          <div className={styles.dropdownContent}>
            {/* No Color Option */}
            <button onClick={() => handleColorSelect(null)} className={styles.option}>
              <div className={`${styles.colorPreview} ${styles.colorPreviewEmpty}`}>
                <IoClose size={12} className={styles.closeIcon} />
              </div>
              <span className={styles.optionText}>
                {withoutColorLabel}
              </span>
              {selectedColor === null && <IoCheckmark size={16} className={styles.checkmark} />}
            </button>

            {/* Color Options */}
            {COLORS.map((color) => (
              <button key={color.value} onClick={() => handleColorSelect(color)} className={styles.option}>
                <div className={styles.colorPreview} style={{ backgroundColor: color.hex }} />
                <span className={styles.optionText}>{color[keyLabel]}</span>
                {selectedColor?.value === color.value && <IoCheckmark size={16} className={styles.checkmark} />}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
